import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, ChevronRight, Check, Info, Search, X } from 'lucide-react';

const MultiLevelProductSelector = ({ 
  selectedCategory, 
  selectedType, 
  onTypeChange, 
  showTip, 
  onTipToggle,
  className = "",
  size = "default" // "default" or "compact"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredType, setHoveredType] = useState(null);
  const [hoveredSubType, setHoveredSubType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState('bottom'); // 'top' or 'bottom'
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  // Calculate dropdown position based on available space
  const calculateDropdownPosition = () => {
    if (!containerRef.current) return 'bottom';
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 400; // Increased max height for better scrolling
    
    // Check if there's enough space below
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      return 'top';
    }
    return 'bottom';
  };

  // Calculate optimal dropdown height based on available space
  const calculateDropdownHeight = () => {
    if (!containerRef.current) return 'max-h-64';
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    
    if (dropdownPosition === 'top') {
      // When dropdown opens above, use space above minus some padding
      const availableHeight = rect.top - 20; // 20px padding from top
      return Math.min(availableHeight, 400); // Max 400px height
    } else {
      // When dropdown opens below, use space below minus some padding
      const availableHeight = viewportHeight - rect.bottom - 20; // 20px padding from bottom
      return Math.min(availableHeight, 400); // Max 400px height
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.multi-level-selector')) {
        setIsOpen(false);
        setSearchQuery(''); // Clear search when closing
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update dropdown position when opening
  useEffect(() => {
    if (isOpen) {
      setDropdownPosition(calculateDropdownPosition());
    }
  }, [isOpen]);

  // Filter types and subtypes based on search query
  const getFilteredTypes = () => {
    if (!selectedCategory?.types) return [];
    
    if (!searchQuery.trim()) {
      return selectedCategory.types;
    }

    const query = searchQuery.toLowerCase();
    return selectedCategory.types.filter(type => {
      // Check if main type matches
      const typeMatches = type.label.toLowerCase().includes(query);
      
      // Check if any subtype matches
      const subTypeMatches = type.subTypes?.some(subType => 
        subType.label.toLowerCase().includes(query)
      );
      
      return typeMatches || subTypeMatches;
    }).map(type => {
      // If main type doesn't match but subtypes do, filter subtypes
      if (!type.label.toLowerCase().includes(query) && type.subTypes) {
        return {
          ...type,
          subTypes: type.subTypes.filter(subType => 
            subType.label.toLowerCase().includes(query)
          )
        };
      }
      return type;
    });
  };

  const handleTypeSelect = (type) => {
    if (type.subTypes && type.subTypes.length > 0) {
      // If type has subtypes, select the first subtype by default
      const firstSubType = type.subTypes[0];
      onTypeChange({
        ...type,
        selectedSubType: firstSubType,
        finalValue: firstSubType.value,
        finalLabel: `${type.label} - ${firstSubType.label}`
      });
    } else {
      // Regular type without subtypes
      onTypeChange({
        ...type,
        selectedSubType: null,
        finalValue: type.value,
        finalLabel: type.label
      });
    }
    setIsOpen(false);
    setSearchQuery(''); // Clear search after selection
  };

  const handleSubTypeSelect = (type, subType) => {
    onTypeChange({
      ...type,
      selectedSubType: subType,
      finalValue: subType.value,
      finalLabel: `${type.label} - ${subType.label}`
    });
    setIsOpen(false);
    setSearchQuery(''); // Clear search after selection
  };

  const getDisplayValue = () => {
    if (!selectedType) return 'Select a type';
    
    if (selectedType.selectedSubType && selectedType.selectedSubType.label) {
      return `${selectedType.label || 'Unknown'} - ${selectedType.selectedSubType.label}`;
    }
    return selectedType.label || 'Select a type';
  };

  const sizeClasses = {
    default: {
      container: "h-10",
      text: "text-sm",
      dropdown: "max-h-64"
    },
    compact: {
      container: "h-8",
      text: "text-xs sm:text-sm",
      dropdown: "max-h-48"
    }
  };

  const currentSize = sizeClasses[size];
  const filteredTypes = getFilteredTypes();
  const dropdownHeight = calculateDropdownHeight();

  // Dynamic dropdown positioning classes
  const getDropdownPositionClasses = () => {
    if (dropdownPosition === 'top') {
      return 'bottom-full mb-1';
    }
    return 'top-full mt-1';
  };

  return (
    <div className={`relative multi-level-selector ${className}`} ref={containerRef}>
      <div className="flex items-center gap-2">
        <div 
          className={`flex-1 border border-gray-200 rounded-lg px-3 py-2 focus-within:outline-none focus-within:ring-2 focus-within:ring-purple-400 ${currentSize.text} cursor-pointer bg-white hover:bg-gray-50 transition-all duration-200 flex items-center justify-between shadow-sm hover:shadow-md hover-lift`}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="truncate font-medium text-gray-700">{getDisplayValue()}</span>
          <ChevronDown 
            size={16} 
            className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          />
        </div>
        <button 
          type="button" 
          onClick={onTipToggle} 
          className="ml-1 p-1 hover:bg-gray-100 rounded transition-colors duration-200"
        >
          <Info size={14} className="text-gray-400" />
        </button>
      </div>

      {/* Dropdown */}
      {isOpen && selectedCategory && selectedCategory.types && (
        <div 
          ref={dropdownRef}
          className={`absolute left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-xl z-50 overflow-hidden animate-in slide-in-from-top-2 duration-200 ${getDropdownPositionClasses()}`}
          style={{ maxHeight: `${dropdownHeight}px` }}
        >
          {/* Search Input */}
          <div className="sticky top-0 bg-white border-b border-gray-100 p-2 z-10">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search product types..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSearchQuery('');
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
                  title="Clear search"
                >
                  <X size={12} className="text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
          </div>

          {/* Filtered Results */}
          <div className="overflow-y-auto" style={{ maxHeight: `${dropdownHeight - 60}px` }}>
            {filteredTypes.length === 0 ? (
              <div className="px-3 py-4 text-center text-gray-500 text-sm">
                {searchQuery ? 'No product types found matching your search.' : 'No product types available.'}
              </div>
            ) : (
              filteredTypes.map((type) => (
                <div key={type.value} className="border-b border-gray-100 last:border-b-0">
                  {/* Main Type Option */}
                  <div
                    className={`px-3 py-2 gradient-purple-pink-hover cursor-pointer transition-all duration-200 ${
                      selectedType && selectedType.value === type.value && !type.subTypes ? 'gradient-purple-pink text-purple-700' : ''
                    }`}
                    onClick={() => handleTypeSelect(type)}
                    onMouseEnter={() => setHoveredType(type.value)}
                    onMouseLeave={() => setHoveredType(null)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{type.label}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {type.subTypes && type.subTypes.length > 0 && (
                          <ChevronRight size={14} className="text-gray-400 transition-transform duration-200" />
                        )}
                        {selectedType && selectedType.value === type.value && !type.subTypes && (
                          <Check size={14} className="text-purple-600" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* SubTypes */}
                  {type.subTypes && type.subTypes.length > 0 && (
                    <div className="bg-gradient-to-r from-gray-50 to-purple-50 border-t border-gray-100">
                      {type.subTypes.map((subType) => (
                        <div
                          key={subType.value}
                          className={`px-6 py-2 gradient-purple-pink-hover cursor-pointer transition-all duration-200 ${
                            selectedType && selectedType.selectedSubType?.value === subType.value ? 'gradient-purple-pink text-purple-700' : ''
                          }`}
                          onClick={() => handleSubTypeSelect(type, subType)}
                          onMouseEnter={() => setHoveredSubType(subType.value)}
                          onMouseLeave={() => setHoveredSubType(null)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium">{subType.label}</span>
                            </div>
                            {selectedType && selectedType.selectedSubType?.value === subType.value && (
                              <Check size={12} className="text-purple-600" />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Tooltip */}
      {showTip && (
        <div className="absolute right-0 top-12 bg-gray-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg z-10 max-w-xs animate-in slide-in-from-top-2 duration-200">
          <div className="font-medium mb-1">Product Type Selection</div>
          <div className="text-gray-300">
            Choose your product type. Some types have subtypes for more specific categorization. Use the search to quickly find what you need.
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiLevelProductSelector;
